import React, { useRef } from 'react'
import { SignupPresenter } from './signup-presenter'
import { signUpSchema } from '../../lib/utils/schema'
import { Card, H2, Flex } from '../../lib/components'
import { useNavigate, useParams } from 'react-router-dom'

import { userServices } from '../../services'
import { useDispatch } from 'react-redux'
import { showBanner } from '../../global-state/banner-slice'
import { createConfiguration } from '../../global-state/investor-slice'
import ReCAPTCHA from 'react-google-recaptcha'
import { captureEvent } from '../../lib/utils/posthogUtils/usePostHog'
import { EVENTS } from '../../lib/utils/constants'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
import { Role } from '../../lib/types'

const initialValues = {
  name: '',
  email: '',
  password: '',
  passwordConfirmation: '',
  referralCode: '',
}

const SignupContainer = () => {
  const reRef = useRef<ReCAPTCHA>(null)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { email } = useParams()

  const handleSubmit_v1 = async (val: any) => {
    const token = await reRef.current?.executeAsync()
    reRef.current?.reset()
    const createUser = await userServices.userSignUp_v1(
      {
        name: val.name,
        email: val.email?.toLowerCase(),
        password: val.password,
        role: Role.investor,
        referralCode: val.referralCode || undefined,
      },
      { 'g-recaptcha-response': token ? token : '' },
    )

    const isUserCreationSuccessful =
      !!createUser && createUser.status === 200 && createUser?.data?.role === Role.investor

    if (isUserCreationSuccessful) {
      dispatch(createConfiguration(createUser.data))
      dispatch(
        showBanner({
          text: 'Please verify your email address',
          variant: 'warning-banner',
        }),
      )

      logCrmEvent(
        EngagementEvents.USER_SIGNED_UP_EMAIL,
        { Authorization: createUser.data.authToken ?? '' },
        { name: createUser.data.name, email: createUser.data.email },
      )

      captureEvent(EVENTS.user_signed_up, {
        email: val.email,
      })

      navigate('/welcome')
    } else if (
      !!createUser &&
      createUser.status === 200 &&
      createUser?.data?.role !== Role.investor
    ) {
      dispatch(showBanner({ text: 'Not allowed to sign up!', variant: 'error-banner' }))
    } else {
      dispatch(
        showBanner({
          text:
            (createUser.data && createUser.data.message) ||
            'Something went wrong! Please try again.',
          variant: 'error-banner',
        }),
      )
    }
  }

  const googleLoginHandler = async (res: any) => {
    const { access_token } = res
    const user = await userServices.googleSignIn_v1({
      accessToken: access_token,
      role: Role.investor,
    })
    if (!!user && user.status === 200 && user?.data?.role === Role.investor) {
      dispatch(createConfiguration({ ...user.data, accountStatus: user.data.account_status }))

      dispatch(showBanner({ text: 'Login Success', variant: 'success-banner' }))

      logCrmEvent(
        EngagementEvents.USER_SIGNED_UP_SOCIAL,
        { Authorization: user.data.authToken },
        { name: user.data.name, email: user.data.email },
      )

      navigate('/')
    } else if (!!user && user.status === 200 && user?.data?.role !== Role.investor) {
      dispatch(showBanner({ text: 'Not allowed to sign up!', variant: 'error-banner' }))
    } else {
      dispatch(
        showBanner({
          text: (user.data && user.data.message) || 'Something went wrong! Please try again.',
          variant: 'error-banner',
        }),
      )
    }
  }

  return (
    <Flex variant='column-center'>
      <H2 text={'Sign Up'} classes={['mb-48']} />
      <Card classes={['p-40']}>
        <SignupPresenter
          initialValues={{ ...initialValues, email: email || '' }}
          validationSchema={signUpSchema}
          handleFormSubmit={handleSubmit_v1}
          reRef={reRef}
          googleLoginHandler={googleLoginHandler}
        />
      </Card>
    </Flex>
  )
}

export { SignupContainer }
